import { useEffect, useState } from "react";
import { FaPlusSquare, FaFileDownload } from "react-icons/fa";
import { LuChevronsDown, LuChevronsUp } from "react-icons/lu";
import { TfiMoney } from "react-icons/tfi";
import RightTimeApi from "@/API";
import { appendBillingOrgQuery } from "@/utils/billingOrgHelpers";
import CreateInvoicesBulkModal from "@elements/modals/CreateInvoicesBulkModal";
import InvoiceDetailModal from "@elements/modals/InvoiceDetailModal";
import PaymentEditModal from "@elements/modals/PaymentEditModal";
import Helpers from "@/Helpers";
import Enums from "@/enums";
import { toast } from "react-toastify";
import { downloadResponseAsFile } from "@/utils/authenticatedFileDownload";

export default function InvoicesTab(props) {
  const { isActive, processInvoiceAsync, formatCurrency, formatDate, updateSpinner, billingOrgId = 0 } = props;
  const [invoiceData, setInvoiceData] = useState<any>(null);
  const [pendingLineItems, setPendingLineItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateInvoicesBulkModal, setShowCreateInvoicesBulkModal] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [invoicesExpanded, setInvoicesExpanded] = useState(true);
  const [paidInvoicesExpanded, setPaidInvoicesExpanded] = useState(false);
  const [pendingLineItemsExpanded, setPendingLineItemsExpanded] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedPaymentInvoice, setSelectedPaymentInvoice] = useState(null);
  const [paymentCompanyId, setPaymentCompanyId] = useState(null);

  useEffect(() => {
    if (isActive) 
      fetchInvoiceData();
  }, [isActive, billingOrgId]);

  async function fetchInvoiceData() {
    updateSpinner && updateSpinner(true);
    setLoading(true);
    setError(null);
    try {
      const resp = await RightTimeApi.getAsync(appendBillingOrgQuery("/Invoices", billingOrgId));
      if (resp && resp.ok && resp.jsonData) {
        setInvoiceData(resp.jsonData);
        // Set pending line items from the same response
        setPendingLineItems(resp.jsonData.pendingLineItems || []);
      } else {
        setError("Failed to load invoice data");
        console.error("Failed to load invoice data:", resp.status, resp.statusText);
      }
    } catch (error) {
      console.error("Error loading invoice data:", error);
      setError("Error loading invoice data: " + error.message);
    } finally {
      setLoading(false);
      updateSpinner && updateSpinner(false);
    }
  }

  function handleCreateNewInvoices() {
    setShowCreateInvoicesBulkModal(true);
  }

  function handleInvoicePaymentClick(e, invoice) {
    e.stopPropagation(); // Prevent row click event
    
    if (!invoice.companyId) {
      toast.error("Unable to create payment - missing company ID");
      return;
    }

    setSelectedPaymentInvoice(invoice);
    setPaymentCompanyId(invoice.companyId);
    setEditingPaymentId(0); // 0 for new payment
  }

  function handlePaymentClick() {
    // For now, we'll use the first active invoice's company ID if available
    // In a more sophisticated implementation, this could open a company selector
    if (invoiceData.activeInvoices && invoiceData.activeInvoices.length > 0) {
      setSelectedPaymentInvoice(null);
      setPaymentCompanyId(0);
      setEditingPaymentId(0); // 0 for new payment
    } else {
      // Could also open with no company selected and let user choose
      setSelectedPaymentInvoice(null);
      setPaymentCompanyId(null);
      setEditingPaymentId(0); // 0 for new payment
    }
  }

  function handleCreateInvoicesBulkModalClose() {
    setShowCreateInvoicesBulkModal(false);
    // Refresh pending invoices after bulk creation
    if (isActive) {
      fetchInvoiceData();
    }
  }

  function handlePaymentEditModalClose() {
    setEditingPaymentId(null);
    setSelectedPaymentInvoice(null);
    setPaymentCompanyId(null);
    // Refresh invoice data after payment recording
    if (isActive) {
      fetchInvoiceData();
    }
  }

  function onToggleCollapse() {
    setInvoicesExpanded(!invoicesExpanded);
  }

  function onTogglePaidCollapse() {
    setPaidInvoicesExpanded(!paidInvoicesExpanded);
  }

  function onTogglePendingLineItemsCollapse() {
    setPendingLineItemsExpanded(!pendingLineItemsExpanded);
  }

  function handleInvoiceClick(invoice) {
    setSelectedInvoice(invoice);
  }

  function handleInvoiceDetailModalClose() {
    setSelectedInvoice(null);
    // Refresh pending invoices after any changes
    if (isActive) {
      fetchInvoiceData();
    }
  }

  async function downloadInvoiceCsv(exportPath, fallbackFilename) {
    try {
      updateSpinner && updateSpinner(true);
      const response = await RightTimeApi.getAsync(appendBillingOrgQuery(exportPath, billingOrgId));
      const ok = await downloadResponseAsFile(response, fallbackFilename);
      if (ok) toast.success("CSV exported successfully");
      else toast.error("Failed to export CSV");
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error("Error exporting CSV");
    } finally {
      updateSpinner && updateSpinner(false);
    }
  }

  async function handleDownloadPdf(e, invoice) {
    e.stopPropagation(); // Prevent row click event
    
    if (!invoice.id || !invoice.companyId) {
      toast.error("Unable to download PDF - missing invoice or company ID");
      return;
    }

    const sid = invoice.statusId;
    if (sid !== Enums.INVOICE_STATUS.Approved
        && sid !== Enums.INVOICE_STATUS.Paid
        && sid !== Enums.INVOICE_STATUS.PartiallyPaid) {
      toast.error("PDF is available only after the invoice has been approved");
      return;
    }

    try {
      updateSpinner && updateSpinner(true);
      const response = await RightTimeApi.getAsync(`/Company/${invoice.companyId}/Invoice/${invoice.id}/Pdf`);
      const ok = await downloadResponseAsFile(
        response,
        `invoice_${invoice.invoiceNumber || invoice.id}.pdf`
      );
      if (ok) toast.success("Invoice PDF downloaded successfully");
      else toast.error("Failed to download invoice PDF");
    } catch (error) {
      console.error("Error downloading invoice PDF:", error);
      toast.error("Error downloading invoice PDF");
    } finally {
      updateSpinner && updateSpinner(false);
    }
  }  

  if(invoiceData && (invoiceData.activeInvoices || invoiceData.paidInvoices)) {
    return (
      <div className="adminInvoicesPanel">
        <div className="invoiceExportToolbar">
          <button
            type="button"
            className="btn btn-outline exportInvoicesCsvButton"
            onClick={() => downloadInvoiceCsv("/Invoices/Export/Summary", "invoices-summary.csv")}
          >
            Export Invoices CSV
          </button>
          <button
            type="button"
            className="btn btn-outline exportLineItemsCsvButton"
            onClick={() => downloadInvoiceCsv("/Invoices/Export/LineItems", "invoice-line-items.csv")}
          >
            Export Line Items CSV
          </button>
        </div>

        {/* Pending Line Items Table */}
        <div className="horizontal">
          <div className={`tableWrapper dimensionTable scrollable collapsable scrollableTableMedium ${pendingLineItemsExpanded ? '' : 'collapsed'}`}
          >
            <table>
              <caption>
                <span className="title">
                  Pending Line Items{pendingLineItems ? (
                    <>
                      <span className="captionCount">({pendingLineItems.length.toString()})</span>
                    </>
                  ) : null}
                </span>
                <span className="captionRight">
                  <span className="toggleCollapse" onClick={onTogglePendingLineItemsCollapse}>
                    <LuChevronsDown className="collapse" />
                    <LuChevronsUp className="expand" />
                  </span>
                </span>
              </caption>
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Test ID</th>
                  <th>Test Date</th>
                  <th>Amount</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {pendingLineItems.length > 0 ? (
                  pendingLineItems
                    .sort((a, b) => new Date(a.chargeDate).getTime() - new Date(b.chargeDate).getTime())
                    .map((lineItem, index) => {
                    const company = invoiceData.companies?.find(c => c.id === lineItem.companyId);
                    return (
                      <tr key={lineItem.id || index}>
                        <th className="left">{company?.name || lineItem.companyName || 'Unknown'}</th>
                        <td>{lineItem.entityId || ''}</td>
                        <td>{Helpers.formatDate(lineItem.chargeDate)}</td>
                        <td className="right">{Helpers.formatCurrency(lineItem.amount)}</td>
                        <td>{lineItem.description || lineItem.testType || ''}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} className="center">No pending line items found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Existing Invoices Tables */}
        <div className="horizontal">
          <div className={`tableWrapper dimensionTable scrollable collapsable scrollableTableMedium ${invoicesExpanded ? '' : 'collapsed'}`} >
            <table>
              <caption>
                <span className="title">
                  Invoices Pending Payment{invoiceData ? (
                    <>
                      <span className="captionCount">({invoiceData.activeInvoices.length.toString()})</span>
                    </>
                  ) : null}
                </span>
                <span className="captionRight" >
                  <span className="toggleCollapse" onClick={onToggleCollapse} >
                    <LuChevronsDown className="collapse" />
                    <LuChevronsUp className="expand" />
                  </span>
                  <span
                    className="actionIcon moneyIcon clickableIcon clickableIcon--spaced"
                    onClick={handlePaymentClick}
                    title="Record Payment"
                  >
                    <TfiMoney />
                  </span>
                  <span
                    className="actionIcon plusIcon clickableIcon clickableIcon--spaced"
                    onClick={handleCreateNewInvoices}
                    title="Create New Invoices"
                  >
                    <FaPlusSquare />
                  </span>
                </span>
              </caption>
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Invoice #</th>
                  <th>Date</th>
                  <th>Total</th>
                  <th>Balance Due</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {invoiceData.activeInvoices.filter(invoice => invoice.statusId !== 1).length > 0 ? (
                  invoiceData.activeInvoices
                    .filter(invoice => invoice.statusId !== 1)
                    .map((invoice) => {
                    const company = invoiceData.companies.find(c => c.id === invoice.companyId);
                    return (
                      <tr key={invoice.id} onClick={() => handleInvoiceClick(invoice)} className="clickableRow">
                        <th className="left">{company?.name}</th>
                        <td>{invoice.id}</td>
                        <td>{Helpers.formatDate(invoice.invoiceDate)}</td>
                        <td className="right">{Helpers.formatCurrency(invoice.totalAmount)}</td>
                        <td className="right">{Helpers.formatCurrency(invoice.balanceDue)}</td>
                        <td className="center">
                          <TfiMoney 
                            className="actionIcon moneyIcon clickableIcon actionIconSpacedRight"
                            onClick={(e) => handleInvoicePaymentClick(e, invoice)}
                            title={`Create Payment for ${Helpers.formatCurrency(invoice.balanceDue)}`}
                          />
                          <FaFileDownload 
                            className="actionIcon downloadIcon clickableIcon"
                            onClick={(e) => handleDownloadPdf(e, invoice)}
                            title="Download PDF"
                          />
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="center">No pending invoices found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="horizontal">
          <div className={`tableWrapper dimensionTable scrollable collapsable scrollableTableMedium ${paidInvoicesExpanded ? '' : 'collapsed'}`}>
            <table>
            <caption>
              <span className="title">
                Paid Invoices{invoiceData ? (
                  <>
                    <span className="captionCount">({invoiceData.paidInvoices?.length || 0})</span>
                  </>
                ) : null}
              </span>
              <span className="captionRight" >
                <span className="toggleCollapse" onClick={onTogglePaidCollapse} >
                  <LuChevronsDown className="collapse" />
                  <LuChevronsUp className="expand" />
                </span>
              </span>
            </caption>
            <thead>
              <tr>
                <th>Company</th>
                <th>Invoice #</th>
                <th>Date</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoiceData.paidInvoices?.length > 0 ? (
                invoiceData.paidInvoices
                  .sort((a, b) => new Date(b.invoiceDate).getTime() - new Date(a.invoiceDate).getTime())
                  .map((invoice) => {
                  return (
                    <tr key={invoice.id} onClick={() => handleInvoiceClick(invoice)} className="clickableRow">
                      <th>{invoice.companyName}</th>
                      <td>{invoice.id}</td>
                      <td>{Helpers.formatDate(invoice.invoiceDate)}</td>
                      <td className="right">{Helpers.formatCurrency(invoice.totalAmount)}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={4} className="center">No fully paid invoices found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </div>

        <CreateInvoicesBulkModal
          isOpen={showCreateInvoicesBulkModal}
          onClose={handleCreateInvoicesBulkModalClose}
          updateSpinner={updateSpinner}
        />

        <PaymentEditModal
          paymentId={editingPaymentId}
          companyId={paymentCompanyId}
          invoiceId={selectedPaymentInvoice ? selectedPaymentInvoice.id : null}
          defaultAmount={selectedPaymentInvoice ? selectedPaymentInvoice.balanceDue : null}
          onClose={handlePaymentEditModalClose}
          updateSpinner={updateSpinner}
        />

        {selectedInvoice && (
          <InvoiceDetailModal
            invoiceId={selectedInvoice.id}
            companyId={selectedInvoice.companyId}
            onClose={handleInvoiceDetailModalClose}
            updateSpinner={updateSpinner}
          />
        )}
      </div>
    );
  }
}


